import React from "react";
import { CartesianGrid, ReferenceArea, ReferenceLine, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis, ZAxis, LabelList, Cell } from "recharts";
import { Card, CardContent } from "../ui/card";
import { FactorTable } from "./Factors";
import { Overline, StockHeader, ThesisStrip } from "./Shared";
import { PEERS, fmtPct } from "./data";

const TT = { background: "#18181b", border: "1px solid #27272a", fontSize: 11 };
const color = (p) => (p.me ? "#38bdf8" : p.lottery ? "#f87171" : p.bp > 0 && p.v3 >= 6 ? "#34d399" : "#71717a");

const PeerMap = () => (
  <div className="h-[380px]" data-testid="kfm-peer-scatter-chart">
    <ResponsiveContainer width="100%" height="100%">
      <ScatterChart margin={{ top: 16, right: 24, bottom: 24, left: 0 }}>
        <CartesianGrid strokeDasharray="2 4" stroke="#27272a" />
        <ReferenceArea x1={0} x2={25} y1={6} y2={13} fill="#34d399" fillOpacity={0.05}
          label={{ value: "CONFIRMED + STRONG MODEL", position: "insideTopRight", fontSize: 10, fill: "#34d399" }} />
        <ReferenceArea x1={-15} x2={0} y1={3} y2={13} fill="#f87171" fillOpacity={0.04}
          label={{ value: "MODEL LIKES IT, PROS UNDERWATER", position: "insideTopLeft", fontSize: 10, fill: "#f87171" }} />
        <XAxis type="number" dataKey="bp" domain={[-15, 25]} ticks={[-15, -10, -5, 0, 5, 10, 15, 20, 25]} tick={{ fontSize: 10, fill: "#71717a" }} tickFormatter={(v) => `${v}%`}
          label={{ value: "Thesis confirmation — sector-adj gain since pros' estimated entry", position: "bottom", offset: 6, fontSize: 10, fill: "#a1a1aa" }} />
        <YAxis type="number" dataKey="v3" domain={[-2, 13]} ticks={[-2, 0, 2, 4, 6, 8, 10, 12]} tick={{ fontSize: 10, fill: "#71717a" }} tickFormatter={(v) => `${v}%`}
          label={{ value: "V3 expected 6m alpha", angle: -90, position: "insideLeft", offset: 14, fontSize: 10, fill: "#a1a1aa" }} />
        <ZAxis type="number" dataKey="holders" range={[60, 420]} />
        <ReferenceLine x={0} stroke="#52525b" /><ReferenceLine y={6} stroke="#52525b" strokeDasharray="3 3" />
        <Tooltip contentStyle={TT} cursor={{ strokeDasharray: "3 3" }}
          formatter={(v, k) => [k === "holders" ? v : `${v}%`, k === "bp" ? "confirmation" : k === "v3" ? "V3 exp." : "holders"]} />
        <Scatter data={PEERS} isAnimationActive={false}>
          {PEERS.map((p) => <Cell key={p.sym} fill={color(p)} fillOpacity={p.me ? 1 : 0.7} stroke={p.me ? "#fff" : "none"} strokeWidth={2} />)}
          <LabelList dataKey="sym" position="top" style={{ fontSize: 10, fill: "#d4d4d8", fontFamily: "monospace" }} />
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  </div>
);

export default function VariantPeers() {
  const better = PEERS.filter((p) => !p.me && !p.lottery && p.bp > 0).sort((a, b) => b.v3 - a.v3).slice(0, 5);
  return (
    <div className="space-y-4" data-testid="kfm-variant-d">
      <StockHeader compact />
      <Card data-testid="kfm-variant-d-hero"><CardContent className="pt-5 pb-5">
        <Overline className="mb-1">Peer positioning — is DELL the best way to own this idea?</Overline>
        <div className="text-[11px] text-muted-foreground mb-3">Technology names with an active 13F cohort · bubble size = institutional holders (crowding)</div>
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-5">
          <div className="lg:col-span-7"><PeerMap /></div>
          <div className="lg:col-span-3 space-y-3" data-testid="kfm-peer-expressions-list">
            <Overline>Better-scored expressions</Overline>
            {better.map((p, i) => (
              <div key={p.sym} className="flex items-center justify-between rounded border border-border/60 px-3 py-2" data-testid={`kfm-peer-${p.sym}`}>
                <div className="flex items-center gap-2"><span className="text-[10px] text-muted-foreground font-mono">{i + 1}</span><span className="font-mono font-bold">{p.sym}</span>
                  <span className="text-[10px] px-1 rounded border border-border text-muted-foreground font-mono">{p.band}</span></div>
                <div className="text-right font-mono text-[11px]"><div className="text-emerald-400">{fmtPct(p.v3)} exp.</div><div className="text-muted-foreground">{fmtPct(p.bp)} conf.</div></div>
              </div>
            ))}
            <div className="rounded border border-red-500/30 bg-red-500/5 px-3 py-2 text-[11px]">
              <span className="font-mono font-bold">SMCI</span> scores S8 but pros are −11.8% underwater → dip-lottery profile (high mean, weak median).
            </div>
            <p className="text-[11px] text-muted-foreground">DELL: confirmed (+4.1%) but a mid-band model reading (+5.0%), just below the green box. Peers inside it combine a stronger model reading with a confirmed thesis.</p>
          </div>
        </div>
      </CardContent></Card>
      <ThesisStrip />
      <FactorTable prefix="kfm-d" />
    </div>
  );
}
